import type { UpsertTeamTaskDto } from "@/modules/boxDashboard/gateway/boxDashboardApiDto"
import { TeamTaskEntity } from "@/modules/boxDashboard/modules/TeamTask/entities/teamTaskEntity"
import { useServicesContext } from "@modules/core/services/services.context"
import { useMutation, useQueryClient, useSuspenseInfiniteQuery } from "@tanstack/react-query"
import { useMemo } from "react"

export const TEAM_TASKS_QUERY_KEY = ["teamTasks"]
export const COMPLETED_TASKS_QUERY_KEY = ["completedTasks"]

export function useTeamTasks(pageSize = 20) {
  const { boxDashboardApi } = useServicesContext()

  const { data, error, fetchNextPage, hasNextPage, refetch, isFetchingNextPage } = useSuspenseInfiniteQuery({
    queryKey: [...TEAM_TASKS_QUERY_KEY, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const result = await boxDashboardApi.getActiveTasks(pageParam, pageSize)
      return {
        ...result,
        tasks: result.content.map((task) => TeamTaskEntity.fromDto(task)),
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.last) {
        return undefined
      }
      return lastPage.page + 1
    },
    initialPageParam: 0,
  })

  const tasks = useMemo(() => data.pages.flatMap((page) => page.tasks), [data.pages])

  return {
    tasks,
    hasNextPage,
    error,
    fetchNextPage,
    refetch,
    isFetchingNextPage,
  }
}

export function useCompletedTasks(pageSize = 20) {
  const { boxDashboardApi } = useServicesContext()

  const { data, error, fetchNextPage, hasNextPage, refetch, isFetchingNextPage } = useSuspenseInfiniteQuery({
    queryKey: [...COMPLETED_TASKS_QUERY_KEY, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const result = await boxDashboardApi.getCompletedTasks(pageParam, pageSize)
      return {
        ...result,
        tasks: result.content.map((task) => TeamTaskEntity.fromDto(task)),
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.last) {
        return undefined
      }
      return lastPage.page + 1
    },
    initialPageParam: 0,
  })

  const tasks = useMemo(() => data.pages.flatMap((page) => page.tasks), [data.pages])

  return {
    tasks,
    hasNextPage,
    error,
    fetchNextPage,
    refetch,
    isFetchingNextPage,
  }
}

export function useCreateTeamTask() {
  const { boxDashboardApi } = useServicesContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpsertTeamTaskDto) => {
      const dto = await boxDashboardApi.createTeamTask(data)
      return TeamTaskEntity.fromDto(dto)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY })
    },
  })
}

export function useUpdateTeamTask() {
  const { boxDashboardApi } = useServicesContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpsertTeamTaskDto }) => {
      const dto = await boxDashboardApi.updateTeamTask(id, data)
      return TeamTaskEntity.fromDto(dto)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY })
    },
  })
}

export function useDeleteTeamTask() {
  const { boxDashboardApi } = useServicesContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await boxDashboardApi.deleteTeamTask(id)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY })
    },
  })
}

export function useToggleTeamTask() {
  const { boxDashboardApi } = useServicesContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, isCompleted }: { id: string; isCompleted: boolean }) => {
      // If currently completed, set back to CREATED, otherwise complete it
      const dto = isCompleted
        ? await boxDashboardApi.updateTeamTask(id, { taskStatus: "CREATED" })
        : await boxDashboardApi.completeTeamTask(id)
      return TeamTaskEntity.fromDto(dto)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY })
    },
  })
}
